import { makeStyles } from '@material-ui/core/styles';
import { Card, CardActionArea, CardContent, Typography, Box } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import { ModelRelease } from '../data/externalArticles';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  cardAction: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  provider: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  newIcon: {
    fontSize: 16,
    color: theme.palette.primary.main,
  },
  externalIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
  date: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(2),
  },
  highlightsLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  highlights: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  highlight: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'flex-start',
    '&:before': {
      content: '"•"',
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
}));

interface ModelReleaseCardProps {
  release: ModelRelease;
}

export const ModelReleaseCard = ({ release }: ModelReleaseCardProps) => {
  const classes = useStyles();

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <Card className={classes.card}>
      <CardActionArea
        className={classes.cardAction}
        component="a"
        href={release.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <CardContent className={classes.content}>
          <Box className={classes.header}>
            <Box className={classes.provider}>
              <NewReleasesIcon className={classes.newIcon} />
              {release.provider}
            </Box>
            <LaunchIcon className={classes.externalIcon} />
          </Box>
          <Typography variant="h6" className={classes.title}>
            {release.title}
          </Typography>
          <Typography className={classes.date}>
            Released {formatDate(release.releaseDate)}
          </Typography>
          <Typography className={classes.highlightsLabel}>Key Highlights:</Typography>
          <Box className={classes.highlights}>
            {release.highlights.map((highlight, index) => (
              <Typography key={index} className={classes.highlight}>
                {highlight}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
